type AnalyticsContext = {
  measurementId: string;
  gtag: (command: 'event' | 'config', ...args: unknown[]) => void;
};

type AnalyticsEventPayload = Record<string, string | number | boolean | null | undefined>;

let missingIdWarned = false;
let missingScriptWarned = false;

const getMeasurementId = (): string | undefined => {
  if (typeof window !== 'undefined' && window.__BRAISE_GA_MEASUREMENT_ID__) {
    return window.__BRAISE_GA_MEASUREMENT_ID__;
  }

  const envValue = import.meta.env?.VITE_GA_MEASUREMENT_ID;
  if (typeof envValue === 'string' && envValue.trim().length > 0) {
    return envValue.trim();
  }

  if (!missingIdWarned) {
    missingIdWarned = true;
    console.warn('[analytics] VITE_GA_MEASUREMENT_ID is not configured; GA events are disabled.');
  }
  return undefined;
};

const getAnalyticsContext = (): AnalyticsContext | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const measurementId = getMeasurementId();
  if (!measurementId) {
    return undefined;
  }

  const gtag = window.gtag;
  if (typeof gtag !== 'function') {
    if (!missingScriptWarned) {
      missingScriptWarned = true;
      console.warn('[analytics] window.gtag is unavailable; ensure the GA script is loaded.');
    }
    return undefined;
  }

  return { measurementId, gtag };
};

export const isAnalyticsReady = (): boolean => Boolean(getAnalyticsContext());

export const trackPageView = (path: string, params?: { title?: string | null; location?: string | null }) => {
  const context = getAnalyticsContext();
  if (!context) {
    return;
  }

  const pageTitle =
    params?.title ??
    (typeof document !== 'undefined' ? document.title : undefined) ??
    undefined;

  const pageLocation = params?.location ?? (typeof window !== 'undefined' ? window.location.href : undefined);

  context.gtag('event', 'page_view', {
    send_to: context.measurementId,
    page_path: path,
    page_title: pageTitle,
    page_location: pageLocation,
  });
};

export const trackAnalyticsEvent = (eventName: string, payload?: AnalyticsEventPayload) => {
  const context = getAnalyticsContext();
  if (!context) {
    return;
  }

  context.gtag('event', eventName, {
    send_to: context.measurementId,
    ...payload,
  });
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (command: 'event' | 'config', ...args: unknown[]) => void;
    __BRAISE_GA_MEASUREMENT_ID__?: string;
  }
}

